import { ISizesMap } from 'productTemplate/types/SizesMap';
import { PRICE_PRIORITY } from 'productTemplate/enums/pricePriority';
import { SHOE_CONDITIONS, NEW_CONDITIONS } from 'shared/enums/ConditionsEnums';

const processOptions = ({
  conditions,
  currentResult,
  isInstantShip,
  options,
  prioritizeFirstOccurence,
}) => {
  const priceType = isInstantShip ? 'instantShipLowestPriceCents' : 'lowestPriceCents';
  let result = currentResult;
  // Sort the options based on priority and if it exists in the options
  const sortedOptions = PRICE_PRIORITY.filter((option) => option in options);

  // Iterate through each option in the size and check if the shoeCondition is in the shoeConditionsArray
  for (const option of sortedOptions) {
    const currentOption = options[option];

    if (conditions.includes(currentOption?.shoeCondition)) {
      const optionPrice = currentOption?.[priceType]?.amount;

      // Check if the current option has a lower price than the lowestPriceResult
      // or if there is no existing lowestPriceResult and the currentOption has an amount
      if (
        (!result && optionPrice > 0) ||
        (optionPrice && optionPrice < result?.[priceType]?.amount)
      ) {
        result = currentOption;
      }

      // Stop iterating after finding the first occurrence if prioritization is enabled.
      // This prioritizes it from bestPrice -> instantShip -> newWithBoxIssues -> newWithDefect
      if (prioritizeFirstOccurence) {
        break;
      }
    }
  }

  return result;
};

/**
 * [getLowestPricedVariant] returns the lowest priced variant from sizesMap
 * within the size, instant ship and condition constraints
 * it will prioritize by sizingQuery size filter if it exists, if no variants exist for it,
 * it will go to the search filters, if no variants exist for it,
 * it will go to the user size preference, if no variants exist for it,
 * it will choose the lowest price variant without any constraints
 */
export const getLowestPricedVariant = ({
  conditions,
  isInstantShip,
  prioritizeFirstOccurence,
  sizes,
  sizesMap,
}: {
  conditions: string[];
  isInstantShip: boolean;
  prioritizeFirstOccurence: boolean;
  sizes: {
    sizingQuery?: string | null;
    sizeSearchFilter: string[] | null;
    userSize: string | null;
  };
  sizesMap: ISizesMap;
}) => {
  const { sizingQuery, sizeSearchFilter, userSize } = sizes;
  let filteredBySizingQuery = null;
  let filteredBySizeSearchFilters = null;
  let filteredByUserSize = null;
  let nonFilteredBySizeResult = null;

  if (!sizesMap) {
    return;
  }

  for (const [size, options] of Object.entries(sizesMap)) {
    // We prioritize by sizingQuery => sizeSearchFilters => userSize => no size restrictions
    const isSizingQueryMatch = sizingQuery && sizingQuery.toString() === size.toString();
    const isSizeSearchFilterMatch = sizeSearchFilter && sizeSearchFilter.includes(size.toString());
    const isUserSizeMatch = userSize && userSize.toString() === size.toString();

    if (isSizingQueryMatch) {
      filteredBySizingQuery = processOptions({
        conditions: [SHOE_CONDITIONS.NEW_NO_DEFECTS], // for sizingQuery, we only want new_no_defects conditions
        currentResult: filteredBySizingQuery,
        isInstantShip,
        options,
        prioritizeFirstOccurence,
      });
    } else if (isSizeSearchFilterMatch) {
      filteredBySizeSearchFilters = processOptions({
        conditions,
        currentResult: filteredBySizeSearchFilters,
        isInstantShip,
        options,
        prioritizeFirstOccurence,
      });
    } else if (isUserSizeMatch) {
      filteredByUserSize = processOptions({
        conditions,
        currentResult: filteredByUserSize,
        isInstantShip,
        options,
        prioritizeFirstOccurence,
      });
    } else {
      nonFilteredBySizeResult = processOptions({
        conditions,
        currentResult: nonFilteredBySizeResult,
        isInstantShip,
        options,
        prioritizeFirstOccurence,
      });
    }
  }

  return (
    filteredBySizingQuery ||
    filteredBySizeSearchFilters ||
    filteredByUserSize ||
    nonFilteredBySizeResult
  );
};

export const getBuyNewButtonVariant = ({
  sizesMap,
  sizes,
  conditions,
  isInstantShip,
}: {
  sizesMap: ISizesMap;
  sizes: {
    sizingQuery: string | null;
    sizeSearchFilter: string[] | null;
    userSize: string | null;
  };
  conditions: string[];
  isInstantShip: boolean;
}) => {
  const filteredConditions = conditions?.filter((condition) => NEW_CONDITIONS.includes(condition));
  const hasConditionsFilter = filteredConditions.length > 0;

  return getLowestPricedVariant({
    conditions: hasConditionsFilter ? filteredConditions : NEW_CONDITIONS,
    isInstantShip,
    prioritizeFirstOccurence: !hasConditionsFilter,
    sizes,
    sizesMap,
  });
};

export const getBuyUsedButtonVariant = ({
  sizesMap,
  sizes,
  conditions,
}: {
  sizesMap: ISizesMap;
  sizes: {
    sizeSearchFilter: string[] | null;
    userSize: string | null;
  };
  conditions: string[];
}) => {
  const USED_CONDITIONS = [SHOE_CONDITIONS.USED, SHOE_CONDITIONS.GOAT_CLEAN];
  const filteredConditions = conditions?.filter((condition) => USED_CONDITIONS.includes(condition));
  const hasConditionsFilter = filteredConditions.length > 0;

  return getLowestPricedVariant({
    conditions: hasConditionsFilter ? filteredConditions : USED_CONDITIONS,
    isInstantShip: false,
    prioritizeFirstOccurence: false,
    sizes,
    sizesMap,
  });
};
